import { useEffect, useCallback, useRef } from "react";

interface HotkeyHandler {
  key: string;
  handler: (event?: KeyboardEvent) => void;
  ctrl?: boolean;
  shift?: boolean;
  meta?: boolean;
}

interface UseHotkeysOptions {
  enabled?: boolean;
  handlers: HotkeyHandler[];
}

/**
 * Hook to register keyboard shortcuts
 *
 * - Supports modifier keys (ctrl, shift, meta)
 * - Can be enabled/disabled dynamically
 * - Automatically cleans up event listeners
 * - Uses ref to avoid re-adding listeners when handlers array changes
 */
export function useHotkeys({ enabled = true, handlers }: UseHotkeysOptions) {
  // Store handlers in a ref to avoid recreating the event listener
  // when the handlers array reference changes (which happens on every render
  // when defined inline). The ref always has the latest handlers.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Find matching handler using the ref (always has latest handlers)
      const matchingHandler = handlersRef.current.find((h) => {
        const keyMatches = event.key === h.key;
        const ctrlMatches = h.ctrl ? event.ctrlKey : !event.ctrlKey;
        const shiftMatches = h.shift ? event.shiftKey : !event.shiftKey;
        const metaMatches = h.meta ? event.metaKey : !event.metaKey;

        return keyMatches && ctrlMatches && shiftMatches && metaMatches;
      });

      if (matchingHandler) {
        event.preventDefault();
        event.stopPropagation();
        matchingHandler.handler(event);
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled) return;

    // Use capture phase to intercept events before they reach child elements
    // This ensures Tab key is handled before browser's default focus behavior
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [enabled, handleKeyDown]);
}
